import * as React from 'react';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';

import { getServerTranslations } from '../../get-server-side-translations';
import { useAuth } from '@restworld/feature-authentication';
import { useOrganizationsService } from '@restworld/data-services';
import {
  orgOnboardingExistingUserFlowAtom,
  orgOnboardingReferralInfoAtom,
  OrgOnboardingReferralInfo,
} from '../../components/organization-onboarding/use-organization-onboarding';
import { InvalidReferralCode } from '../../components/organization-onboarding/invalid-referral-code';

const OrganizationOnboarding = dynamic(() => import('../../components/organization-onboarding/onboarding'), {
  ssr: false,
});

const BASE_URL = process.env.VERCEL_URL || '';

const CANONICAL_URL = `${BASE_URL}/getting-started`;

const OG = {
  title: "Crea un'offerta di lavoro su Restworld",
  description:
    'Pubblica un annuncio in 3 minuti. Noi contattiamo, intervistiamo e filtriamo i candidati per te. Sospendi quando vuoi.',
  image: `${BASE_URL}/utils-assets/og-getting-started-generic.png`,
};

const NewEmployersPage: React.FunctionComponent = () => {
  const router = useRouter();
  const { isAuthenticated, isInitialized } = useAuth();
  const isExistingUserFlow = useAtomValue(orgOnboardingExistingUserFlowAtom);
  const setReferralInfo = useSetAtom(orgOnboardingReferralInfoAtom);
  const orgService = useOrganizationsService();

  // Track whether referral lookup is in progress
  const [isReferralLoading, setIsReferralLoading] = React.useState(true);

  // Track whether referral code is invalid
  const [isInvalidReferralCode, setIsInvalidReferralCode] = React.useState(false);

  // Track whether user was authenticated when page first loaded
  // This prevents redirecting users who become authenticated during the onboarding flow
  const wasAuthenticatedOnMount = React.useRef<boolean | null>(null);

  // Capture authentication state on initial load
  React.useEffect(() => {
    if (isInitialized && wasAuthenticatedOnMount.current === null) {
      wasAuthenticatedOnMount.current = isAuthenticated;
    }
  }, [isAuthenticated, isInitialized]);

  // Redirect authenticated users away before doing anything else
  // EXCEPTION: If the existing user flow flag is set (user logged in from org onboarding),
  // skip the redirect so we can create entities with their onboarding atom data
  React.useEffect(() => {
    if (isInitialized && wasAuthenticatedOnMount.current === true && !isExistingUserFlow) {
      router.replace('/employer/homepage');
    }
  }, [isInitialized, router, isExistingUserFlow]);

  // Resolve referral code from ?ref= URL param AFTER auth check
  React.useEffect(() => {
    if (!router.isReady || !isInitialized) return;

    // Skip referral lookup if user is already authenticated (will be redirected)
    if (wasAuthenticatedOnMount.current === true && !isExistingUserFlow) return;

    const refCode = router.query.ref as string | undefined;
    if (!refCode) {
      // No referral code — proceed immediately
      setIsReferralLoading(false);
      return;
    }

    // Fetch referral info from backend
    orgService
      .lookupReferralCode(refCode)
      .then((response) => {
        const data = response.data;
        const info: OrgOnboardingReferralInfo = {
          referral_code: data.referral_code,
          referrer_restaurant_name: data.referrer_restaurant_name,
          referrer_organization_id: data.referrer_organization_id,
        };
        setReferralInfo(info);
      })
      .catch(() => {
        // Invalid referral code — show error page
        setReferralInfo(null);
        setIsInvalidReferralCode(true);
      })
      .finally(() => {
        // Always stop loading after lookup completes (success or failure)
        setIsReferralLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.ref, isInitialized]);

  // Handle "proceed without invite link" — clear referral state and continue normal flow
  const handleProceedWithout = React.useCallback(() => {
    setIsInvalidReferralCode(false);
    setReferralInfo(null);
    // Remove ?ref= from URL without triggering a re-render/navigation
    const { ref, ...restQuery } = router.query;
    router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
  }, [router, setReferralInfo]);

  const ogHead = (
    <Head>
      <title>{OG.title}</title>
      <link rel="canonical" href={CANONICAL_URL} />
      <meta name="description" content={OG.description} />
      <meta property="og:title" content={OG.title} />
      <meta property="og:description" content={OG.description} />
      <meta property="og:image" content={OG.image} />
      <meta property="og:url" content={CANONICAL_URL} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={OG.title} />
      <meta name="twitter:description" content={OG.description} />
      <meta name="twitter:image" content={OG.image} />
    </Head>
  );

  // Don't render onboarding if user was already authenticated on mount
  // (unless they're returning from the existing user login flow)
  if (wasAuthenticatedOnMount.current === true && !isExistingUserFlow) {
    return ogHead;
  }

  // Wait for referral lookup to complete before rendering onboarding
  // This prevents the flash of normal screen before referral intro screen appears
  if (isReferralLoading) {
    return ogHead;
  }

  // Show error page when referral code from URL is invalid
  if (isInvalidReferralCode) {
    return (
      <>
        {ogHead}
        <InvalidReferralCode
          onGoBack={() => router.push('/ask-for-referral')}
          onProceedWithout={handleProceedWithout}
        />
      </>
    );
  }

  return (
    <>
      {ogHead}
      <OrganizationOnboarding />
    </>
  );
};

export default NewEmployersPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await getServerTranslations(locale, [
    'common',
    'jp-onboarding',
    'restaurant-onboarding',
    'job-positions-page',
    'job-position-page',
    'payment-and-invoices',
  ]);
  return {
    props: {
      ...translation,
    },
  };
};
